export interface NavItem {
  label: string;
  path: string;
  /** key into the `navIcons` map (Task 7); must exist there */
  icon: string;
  /** extra path prefixes that also mark this item active */
  matches?: string[];
}
export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  { label: 'Overview', items: [{ label: 'Dashboard', path: '/dashboard', icon: 'dashboard' }] },
  {
    label: 'Workloads',
    items: [
      { label: 'Apps', path: '/apps', icon: 'apps' },
      { label: 'Catalog', path: '/catalog', icon: 'catalog' },
      { label: 'External Users', path: '/external-users', icon: 'users' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { label: 'Instances', path: '/instances', icon: 'instances' },
      { label: 'Endpoints', path: '/resources/endpoints', icon: 'endpoints' },
      { label: 'Containers', path: '/resources/containers', icon: 'containers' },
      { label: 'Stacks & Services', path: '/resources/stacks', icon: 'stacks', matches: ['/resources/services'] },
      { label: 'Volumes & Networks', path: '/resources/volumes', icon: 'volumes', matches: ['/resources/networks'] },
    ],
  },
  {
    label: 'Integrate',
    items: [
      { label: 'API Docs', path: '/api-docs', icon: 'docs' },
      { label: 'API Keys', path: '/api-keys', icon: 'keys' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Audit Logs', path: '/audit-logs', icon: 'audit' },
      // `path` is the landing tab; `matches` widens the active state to the whole section
      { label: 'Settings', path: '/settings/security-blocks', icon: 'settings', matches: ['/settings'] },
    ],
  },
];

export const allNavItems: NavItem[] = navGroups.flatMap((g) => g.items);

const underPath = (pathname: string, base: string) => pathname === base || pathname.startsWith(base + '/');

/** Callers must pass `location.pathname` (no search string or hash). */
export function isActive(item: NavItem, pathname: string): boolean {
  pathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return [item.path, ...(item.matches ?? [])].some((p) => underPath(pathname, p));
}

const segmentLabels: Record<string, string> = {
  dashboard: 'Dashboard', instances: 'Instances', resources: 'Resources', containers: 'Containers',
  stacks: 'Stacks', services: 'Services', volumes: 'Volumes', networks: 'Networks',
  endpoints: 'Endpoints', catalog: 'Catalog', apps: 'Apps', new: 'New', 'external-users': 'External Users',
  'api-docs': 'API Docs', 'api-keys': 'API Keys', 'audit-logs': 'Audit Logs', settings: 'Settings',
  'security-blocks': 'Security Blocks', domains: 'Domains', webhooks: 'Webhooks',
};

const safeDecode = (s: string) => {
  try { return decodeURIComponent(s); } catch { return s; }
};

export interface Crumb { label: string; to: string; /** false when the path has no page of its own */ link: boolean }

/** Paths that exist only as a URL prefix; there is no route to link to. */
export const hasPage = (path: string) => path !== '/settings' && path !== '/resources' && !/^\/catalog\/[^/]+\/stacks$/.test(path);

export function buildBreadcrumbs(pathname: string): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  return parts.map((seg, i) => {
    const to = '/' + parts.slice(0, i + 1).join('/');
    return { label: segmentLabels[seg] ?? safeDecode(seg), to, link: hasPage(to) };
  });
}
